import type { SegmentData, StopBlock } from "../types";
import { findGroupBlock, getGroupStopIds } from "./groupHelpers";
import { getKmDistanceBetweenCoordinates } from "./mapHelpers";

export type Coordinates = [number, number];

export type StopWithCoordinates = {
  item: StopBlock;
  coordinates: Coordinates;
};

// ── Pure helpers ─────────────────────────────────────────────────────────────

/** Extract [lng, lat] pairs from a GeoJSON outline (polygon or trail line). */
export function extractOutlineCoordinates(
  outline?: GeoJSON.Geometry | null,
): Coordinates[] {
  if (!outline) return [];
  if (outline.type === "Polygon") {
    return outline.coordinates[0] as Coordinates[];
  }
  if (outline.type === "MultiPolygon") {
    return outline.coordinates.flatMap(poly => poly[0]) as Coordinates[];
  }
  if (outline.type === "LineString") {
    return outline.coordinates as Coordinates[];
  }
  if (outline.type === "MultiLineString") {
    return outline.coordinates.flatMap(line => line) as Coordinates[];
  }
  return [];
}

/** Distance-based fly duration matching the Tripper prototype. */
export function flyDuration(distKm: number, isFirst: boolean): number {
  if (isFirst) return 2000;
  if (distKm < 2) return 1500;
  if (distKm < 20) return 1500 + (distKm / 20) * 1500;
  return 3000 + Math.min(distKm / 80, 1) * 1500;
}

// ── Camera intent types ──────────────────────────────────────────────────────

export type CameraIntent =
  | { type: "none" }
  | { type: "overview"; animated: boolean }
  | { type: "group-entry"; groupId: string; allCoords: Coordinates[] }
  | { type: "hold"; updatePrevCoords?: Coordinates }
  | {
      type: "fly-to-stop";
      coords: Coordinates;
      bearing: number;
      zoom: number;
      pitch: number;
      duration: number;
    }
  | { type: "warn-missing-coords"; stopId: string };

export type CameraRefState = {
  prevStopCoords: Coordinates | null;
  prevGroupId: string | null;
  hasInitialAnimated: boolean;
};

export type CameraIntentResult = {
  intent: CameraIntent;
  nextState: CameraRefState;
};

export type CameraIntentInput = {
  isAutoFollowActive: boolean;
  isMetadataInView: boolean;
  activeItemId: string | null;
  activeStopGroupId: string | null;
  hasBounds: boolean;
  stopsWithCoordinates: StopWithCoordinates[];
  groupTypeById: Map<string, string>;
  segments: SegmentData[];
  state: CameraRefState;
};

// ── Pure decision function ───────────────────────────────────────────────────

export function deriveCameraIntent(input: CameraIntentInput): CameraIntentResult {
  const {
    isAutoFollowActive,
    isMetadataInView,
    activeItemId,
    activeStopGroupId,
    hasBounds,
    stopsWithCoordinates,
    groupTypeById,
    segments,
    state,
  } = input;

  let { prevStopCoords, prevGroupId, hasInitialAnimated } = state;

  // Auto-follow disabled — no camera action
  if (!isAutoFollowActive) {
    return { intent: { type: "none" }, nextState: state };
  }

  // 1. Metadata in view → overview
  if (isMetadataInView) {
    if (!hasBounds) {
      return { intent: { type: "none" }, nextState: state };
    }
    if (!hasInitialAnimated) {
      return {
        intent: { type: "none" },
        nextState: {
          prevStopCoords: null,
          prevGroupId: null,
          hasInitialAnimated: true,
        },
      };
    }
    const animated = prevStopCoords !== null;
    return {
      intent: { type: "overview", animated },
      nextState: {
        prevStopCoords: null,
        prevGroupId: null,
        hasInitialAnimated,
      },
    };
  }

  // 2. Group entry
  const inGroup = Boolean(activeStopGroupId);
  const groupChanged = activeStopGroupId !== prevGroupId;

  if (inGroup && groupChanged) {
    const nextGroupId = activeStopGroupId ?? null;
    const groupBlock = findGroupBlock(segments, activeStopGroupId);

    if (groupBlock) {
      const groupStopIds = getGroupStopIds(groupBlock);
      const groupCoords = stopsWithCoordinates
        .filter(s => groupStopIds.has(s.item._id))
        .map(s => s.coordinates);
      const boundaryCoords = extractOutlineCoordinates(groupBlock.place?.outline);
      const allCoords = [...groupCoords, ...boundaryCoords];

      return {
        intent: { type: "group-entry", groupId: nextGroupId!, allCoords },
        nextState: { prevStopCoords, prevGroupId: nextGroupId, hasInitialAnimated },
      };
    }

    // Group block not found — still update ref
    return {
      intent: { type: "none" },
      nextState: { prevStopCoords, prevGroupId: nextGroupId, hasInitialAnimated },
    };
  }

  // 3. Leaving a group — hold if activeItem still belongs to previous group
  if (!inGroup && prevGroupId) {
    const prevGroupBlock = findGroupBlock(segments, prevGroupId);
    if (prevGroupBlock) {
      const prevGroupStopIds = getGroupStopIds(prevGroupBlock);
      if (activeItemId && prevGroupStopIds.has(activeItemId)) {
        return {
          intent: { type: "hold" },
          nextState: { prevStopCoords, prevGroupId, hasInitialAnimated },
        };
      }
    }
    prevGroupId = null;
  }

  // 4. Active stop lookup
  const activeStop = stopsWithCoordinates.find(s => s.item._id === activeItemId);
  if (!activeStop) {
    if (activeItemId) {
      return {
        intent: { type: "warn-missing-coords", stopId: activeItemId },
        nextState: { prevStopCoords, prevGroupId, hasInitialAnimated },
      };
    }
    return {
      intent: { type: "none" },
      nextState: { prevStopCoords, prevGroupId, hasInitialAnimated },
    };
  }

  const coords = activeStop.coordinates;
  const markerNum = stopsWithCoordinates.indexOf(activeStop) + 1;
  const bearing = ((markerNum % 5) - 2) * 12;

  const groupType = activeStopGroupId
    ? (groupTypeById.get(activeStopGroupId) ?? "SEQUENTIAL")
    : null;
  const isOptionsGroup = groupType === "OPTIONS";

  // 5. OPTIONS group — hold camera, but update prevCoords for distance calc later
  if (inGroup && isOptionsGroup) {
    return {
      intent: { type: "hold", updatePrevCoords: coords },
      nextState: { prevStopCoords: coords, prevGroupId, hasInitialAnimated },
    };
  }

  // 6. Sequential group or standalone stop — fly to it
  const distKm = prevStopCoords
    ? getKmDistanceBetweenCoordinates(prevStopCoords, coords)
    : 0;
  const isFirst = !prevStopCoords;

  return {
    intent: {
      type: "fly-to-stop",
      coords,
      bearing,
      zoom: inGroup ? 15 : 17.5,
      pitch: inGroup ? 40 : 55,
      duration: flyDuration(distKm, isFirst),
    },
    nextState: { prevStopCoords: coords, prevGroupId, hasInitialAnimated },
  };
}
